import { type ShiftMigrationResponse } from "@clipboard-health/contract-backend-main";
import { endOfDay } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import type { TuFunction } from "@src/appV2/i18n/types";
import { useTu } from "@src/appV2/i18n/useTu";
import { formatCbhApiError, isCbhApiAxiosError } from "@src/appV2/lib/utils/errorUtils";
import { type UseModalState } from "@src/lib/ui-react";
import { useState } from "react";

import { DialogFooter } from "../../components/DialogFooter";
import { ResponsiveDialog } from "../../components/ResponsiveDialog";
import { BerlinThemeProvider } from "../../theme/ThemeProvider";
import { useCreateShiftMigration } from "../api/useCreateShiftMigration";
import {
  type ShiftMigrationDestinationWorkplace,
  type ShiftMigrationOriginWorkplace,
} from "../types";
import { type ShiftMigrationParametersSubmitValues } from "./shiftMigrationParametersForm";
import { ShiftMigrationParametersStep } from "./ShiftMigrationParametersStep";
import { ShiftMigrationPreviewStep } from "./ShiftMigrationPreviewStep";
import { ShiftMigrationResultStep } from "./ShiftMigrationResultStep";

const PARAMETERS_FORM_ID = "shift-migration-parameters-form";

type DialogStep = "parameters" | "preview" | "result-success" | "result-error";

interface DraftParameters {
  destination: ShiftMigrationDestinationWorkplace;
  effectiveStarting: Date;
  effectiveEnding?: Date;
  qualification?: string;
}

function getErrorMessage(error: unknown, tu: TuFunction): string {
  if (isCbhApiAxiosError(error)) {
    const firstError = error.response.data.errors[0];
    if (isDefined(firstError)) {
      const formatted = formatCbhApiError(firstError);
      if (formatted.length > 0) {
        return formatted;
      }
    }
  }
  return tu("Migration failed. Please try again or contact support.");
}

export interface ShiftMigrationDialogProps {
  modalState: UseModalState;
  origin: ShiftMigrationOriginWorkplace;
}

export function ShiftMigrationDialog(props: ShiftMigrationDialogProps) {
  const { tu } = useTu("shiftMigration");

  const { modalState, origin } = props;

  const [step, setStep] = useState<DialogStep>("parameters");
  const [draft, setDraft] = useState<DraftParameters | undefined>(undefined);
  const [preview, setPreview] = useState<ShiftMigrationResponse | undefined>(undefined);
  const [executionResult, setExecutionResult] = useState<ShiftMigrationResponse | undefined>(
    undefined
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const { mutateAsync: createShiftMigration, isLoading } = useCreateShiftMigration();

  const resetAndClose = () => {
    setStep("parameters");
    setDraft(undefined);
    setPreview(undefined);
    setExecutionResult(undefined);
    setErrorMessage(undefined);
    modalState.closeModal();
  };

  const handleParametersSubmit = async (values: ShiftMigrationParametersSubmitValues) => {
    setDraft(values);
    setErrorMessage(undefined);
    // The backend uses exclusive end dates, so we have to shift to the start of the following day in the workplace's tz.
    const effectiveEnding = isDefined(values.effectiveEnding)
      ? new Date(endOfDay(values.effectiveEnding, { timeZone: origin.tmz }).getTime() + 1)
      : undefined;
    try {
      const response = await createShiftMigration({
        originFacilityId: origin.id,
        destinationFacilityId: values.destination.id,
        effectiveStarting: values.effectiveStarting,
        effectiveEnding,
        qualification: values.qualification,
        dryRun: true,
      });
      setPreview(response);
      setStep("preview");
    } catch (error) {
      setErrorMessage(getErrorMessage(error, tu));
      setStep("result-error");
    }
  };

  const handleConfirm = async () => {
    if (!isDefined(draft) || !isDefined(preview)) {
      return;
    }
    setErrorMessage(undefined);
    // The backend uses exclusive end dates, so we have to shift to the start of the following day in the workplace's tz.
    const effectiveEnding = isDefined(draft.effectiveEnding)
      ? new Date(endOfDay(draft.effectiveEnding, { timeZone: origin.tmz }).getTime() + 1)
      : undefined;
    try {
      const response = await createShiftMigration({
        originFacilityId: origin.id,
        destinationFacilityId: draft.destination.id,
        effectiveStarting: draft.effectiveStarting,
        effectiveEnding,
        qualification: draft.qualification,
        dryRun: false,
        expectedShiftsHash: preview.shiftsHash,
      });
      setExecutionResult(response);
      setStep("result-success");
    } catch (error) {
      setErrorMessage(getErrorMessage(error, tu));
      setStep("result-error");
    }
  };

  const handleBackToParameters = () => {
    setPreview(undefined);
    setErrorMessage(undefined);
    setStep("parameters");
  };

  let title: string;
  let footer: React.ReactNode;
  let body: React.ReactNode;

  if (step === "parameters") {
    title = tu("Migrate shifts");
    body = (
      <ShiftMigrationParametersStep
        formId={PARAMETERS_FORM_ID}
        origin={origin}
        defaults={
          isDefined(draft)
            ? {
                destination: draft.destination,
                effectiveStarting: draft.effectiveStarting,
                effectiveEnding: draft.effectiveEnding,
                qualification: draft.qualification ?? "",
              }
            : undefined
        }
        isSubmitting={isLoading}
        onSubmit={(values) => {
          void handleParametersSubmit(values);
        }}
      />
    );
    footer = (
      <DialogFooter
        primaryAction={{
          label: tu("Preview migration"),
          isLoading,
          // The form submit is wired via `form` attribute on the button below.
          onClick: () => {
            const formElement = document.getElementById(PARAMETERS_FORM_ID);
            if (formElement instanceof HTMLFormElement) {
              formElement.requestSubmit();
            }
          },
        }}
        secondaryAction={{
          label: tu("Cancel"),
          disabled: isLoading,
          onClick: resetAndClose,
        }}
      />
    );
  } else if (step === "preview" && isDefined(draft) && isDefined(preview)) {
    title = tu("Confirm migration");
    body = (
      <ShiftMigrationPreviewStep
        origin={origin}
        destination={draft.destination}
        effectiveStarting={draft.effectiveStarting}
        effectiveEnding={draft.effectiveEnding}
        qualification={draft.qualification}
        counts={preview.counts}
      />
    );
    footer = (
      <DialogFooter
        primaryAction={{
          label: tu("Confirm migration"),
          isLoading,
          onClick: () => {
            void handleConfirm();
          },
        }}
        secondaryAction={{
          label: tu("Back"),
          disabled: isLoading,
          onClick: handleBackToParameters,
        }}
      />
    );
  } else if (step === "result-success" && isDefined(executionResult)) {
    title = tu("Migration complete");
    body = (
      <ShiftMigrationResultStep
        counts={executionResult.counts}
        qualification={draft?.qualification}
        variant="success"
      />
    );
    footer = (
      <DialogFooter
        primaryAction={{
          label: tu("Close"),
          onClick: resetAndClose,
        }}
      />
    );
  } else {
    title = tu("Migration failed");
    body = <ShiftMigrationResultStep errorMessage={errorMessage} variant="error" />;
    footer = (
      <DialogFooter
        primaryAction={{
          label: tu("Back to parameters"),
          onClick: handleBackToParameters,
        }}
        secondaryAction={{
          label: tu("Close"),
          onClick: resetAndClose,
        }}
      />
    );
  }

  return (
    <BerlinThemeProvider>
      <ResponsiveDialog
        showCloseButton
        footerContent={footer}
        modalState={modalState}
        title={title}
        width={520}
        onClose={resetAndClose}
      >
        {body}
      </ResponsiveDialog>
    </BerlinThemeProvider>
  );
}
